import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import { useSelector } from 'react-redux';
import MainLayout from '../../../layout/main-layout';
import { APP_API_URL } from '../../../utils';
import formatNumber, { defaultFilterDates, formatDate } from '../../../utils/functions';

const PER_PAGE = 10;
const beneficiaryLabel = (type) => (type === 'personne' ? 'Personne' : 'Association / ONG');

export default function DonationsList() {
  const user = useSelector((state) => state.auth.user);
  const isRole13 = Number(user?.personnels?.[0]?.poste_id) === 13;
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [appliedFilters, setAppliedFilters] = useState(() => ({ search: '', ...defaultFilterDates() }));
  const [draftFilters, setDraftFilters] = useState(() => defaultFilterDates());
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${APP_API_URL}/donations`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          fromDate: appliedFilters.dateFrom || undefined,
          toDate: appliedFilters.dateTo || undefined,
          search: appliedFilters.search || undefined,
        },
      });
      setRows(response.data?.donations || []);
    } catch (error) {
      console.error(error);
      setRows([]);
    } finally {
      setIsLoading(false);
    }
  }, [appliedFilters]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setAppliedFilters((prev) => (prev.search === searchInput ? prev : { ...prev, search: searchInput }));
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const stats = useMemo(() => ({
    total: rows.length,
    cost: rows.reduce((acc, row) => acc + (parseFloat(row.cost_total) || 0), 0),
  }), [rows]);

  const totalPages = Math.max(1, Math.ceil(rows.length / PER_PAGE));
  const indexOfFirst = (currentPage - 1) * PER_PAGE;
  const currentItems = rows.slice(indexOfFirst, indexOfFirst + PER_PAGE);

  const handleCancel = async (id) => {
    const result = await Swal.fire({
      title: 'Annuler ce don ?',
      text: 'Les quantités retournent dans le stock d’origine. Aucune facture n’est concernée.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Oui, annuler',
      cancelButtonText: 'Non',
    });
    if (!result.isConfirmed) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${APP_API_URL}/donations/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchData();
    } catch (error) {
      Swal.fire({ icon: 'error', text: error.response?.data?.message || 'Annulation impossible.' });
    }
  };

  return (
    <MainLayout>
      <div className="duo-page">
        <div className="duo-hero duo-hero--compact mb-3">
          <p className="duo-hero-sub mb-1">Sans facture</p>
          <h1 className="duo-hero-title">Dons</h1>
          <p className="duo-hero-sub mt-2 mb-0">
            Sortie de stock offerte à une association, une ONG ou une personne.
          </p>
        </div>
        <div className="duo-kpi-row">
          <div className="duo-kpi-chip">
            <p className="duo-kpi-chip__label">Dons</p>
            <p className="duo-kpi-chip__value">{stats.total}</p>
          </div>
          <div className="duo-kpi-chip">
            <p className="duo-kpi-chip__label">Coût de revient donné</p>
            <p className="duo-kpi-chip__value" style={{ fontSize: '1rem' }}>{formatNumber(stats.cost)} XOF</p>
          </div>
        </div>
        <section className="duo-panel">
          <div className="duo-toolbar">
            <span className="fw-bold">Liste des dons</span>
            <div className="duo-toolbar__actions">
              <button type="button" className="duo-btn duo-btn--ghost duo-btn--icon" onClick={fetchData} title="Rafraîchir">
                <i className="bi bi-arrow-clockwise" />
              </button>
              <button type="button" className="duo-btn duo-btn--ghost duo-btn--icon" onClick={() => setFiltersOpen((open) => !open)}>
                <i className={`bi ${filtersOpen ? 'bi-x-lg' : 'bi-funnel-fill'}`} />
              </button>
              {!isRole13 && (
                <Link to="/admin/dons/nouveau" className="duo-btn duo-btn--primary">
                  <i className="bi bi-gift" /> Nouveau don
                </Link>
              )}
            </div>
          </div>
          <div className="px-3 pt-2 pb-2">
            <input
              type="search"
              className="duo-input"
              placeholder="Association, personne, note…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>
          {filtersOpen && (
            <form
              className="duo-filters"
              onSubmit={(e) => {
                e.preventDefault();
                setAppliedFilters((prev) => ({ ...prev, ...draftFilters }));
                setCurrentPage(1);
              }}
            >
              <div className="duo-filters__grid">
                <div>
                  <label className="duo-form-label">Date de début</label>
                  <input type="date" className="duo-input" value={draftFilters.dateFrom} onChange={(e) => setDraftFilters((prev) => ({ ...prev, dateFrom: e.target.value }))} />
                </div>
                <div>
                  <label className="duo-form-label">Date de fin</label>
                  <input type="date" className="duo-input" value={draftFilters.dateTo} onChange={(e) => setDraftFilters((prev) => ({ ...prev, dateTo: e.target.value }))} />
                </div>
              </div>
              <div className="d-flex justify-content-end">
                <button type="submit" className="duo-btn duo-btn--primary">Appliquer</button>
              </div>
            </form>
          )}
          <div className="px-3 py-2 small text-muted">
            Plage : {appliedFilters.dateFrom} → {appliedFilters.dateTo}
          </div>
          <div className="duo-table-wrap">
            <table className="duo-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Bénéficiaire</th>
                  <th>Type</th>
                  <th>Articles</th>
                  <th>Coût</th>
                  <th>Note</th>
                  <th>Par</th>
                  {!isRole13 && <th />}
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr><td colSpan={8} className="duo-empty-row">Chargement des dons…</td></tr>
                ) : currentItems.length > 0 ? currentItems.map((row) => (
                  <tr key={row.id}>
                    <td>{formatDate(row.created_at)}</td>
                    <td className="fw-bold">{row.beneficiary_name}</td>
                    <td>{beneficiaryLabel(row.beneficiary_type)}</td>
                    <td>
                      {(row.items || []).map((item) => `${item.name} (${item.quantity} ${item.unit || ''})`).join(', ')}
                    </td>
                    <td>{formatNumber(row.cost_total)} XOF</td>
                    <td>{row.note || '—'}</td>
                    <td>{row.user?.name || '—'}</td>
                    {!isRole13 && (
                      <td>
                        <button type="button" className="duo-btn duo-btn--ghost duo-btn--sm" onClick={() => handleCancel(row.id)}>
                          Annuler
                        </button>
                      </td>
                    )}
                  </tr>
                )) : (
                  <tr><td colSpan={8} className="duo-empty-row">Aucun don sur cette période.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          {(rows.length > 0 || isLoading) && (
            <div className="duo-footer-bar">
              <span>{isLoading ? 'Chargement…' : `${indexOfFirst + 1} – ${Math.min(indexOfFirst + PER_PAGE, rows.length)} sur ${rows.length}`}</span>
              <ul className="duo-pagination">
                <li><button type="button" className="duo-btn duo-btn--ghost" disabled={isLoading || currentPage === 1} onClick={() => setCurrentPage((p) => p - 1)}>Précédent</button></li>
                <li><span className="duo-pagination__info px-2">Page {currentPage} / {totalPages}</span></li>
                <li><button type="button" className="duo-btn duo-btn--primary" disabled={isLoading || currentPage >= totalPages} onClick={() => setCurrentPage((p) => p + 1)}>Suivant</button></li>
              </ul>
            </div>
          )}
        </section>
      </div>
    </MainLayout>
  );
}
